import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Spin } from 'antd';
import squareCatalogApi from '../../../services/squareCatalogApi';
import useSquareIntegrated from './use-square-integrated';

// Menu-editor awareness band for webhook syncs: "Syncing with Square…" while
// a sync holds the per-company lock, then "Menu updated from Square." with a
// Refresh action once lastsyncat moves past the baseline captured on mount.
// Self-gated: renders nothing for non-Square companies, missing permission,
// old APIs (no inprogress field → only the updated-state works), or idle.
const POLL_MS = 30000;
const POLL_FAST_MS = 5000;

const SquareSyncActivityBand = ({ onRefresh }) => {
  const { integrated } = useSquareIntegrated();
  const globalCompany = useSelector((state) => state.companyReducer.selected);
  const rolepermissions = useSelector((state) => state.authReducer.rolepermissions);
  const canView = !!rolepermissions?.includes('squarecatalog-sync');
  const companyId = globalCompany?.id;

  const [syncing, setSyncing] = useState(false);
  const [updated, setUpdated] = useState(false);
  const baselineRef = useRef(null);
  const timeoutRef = useRef(null);

  // Identity-only reset/start: this effect's deps are exactly [integrated,
  // canView, companyId], so it runs once per real identity change — never on
  // a `syncing`/`updated` transition. Baseline and the updated flag survive
  // syncing flips; they're only cleared here (company/permission/integration
  // change) or by the Refresh click below.
  //
  // The poll chain lives entirely inside this effect (self-rescheduling
  // setTimeout, NOT setInterval keyed on `syncing` state) so a poll's own
  // inprogress result decides its own next delay without ever re-running the
  // effect — see prior fix for why `syncing` must never be a dependency here.
  //
  // `cancelled` is this effect run's own generation flag, not just a
  // clearTimeout of a *pending* timer: clearTimeout can't stop a fetch that's
  // already in flight. Without this flag, unmounting (or an identity change)
  // while a poll's `await` is outstanding lets that stale poll's response
  // still land — writing stale/wrong-company state and re-arming its own
  // timeout chain (orphaned and uncancelable, since cleanup already ran and
  // has no reference to it) once it resolves. Checking `cancelled` right
  // after the await, before any setState or reschedule, closes that gap.
  useEffect(() => {
    baselineRef.current = null;
    setUpdated(false);
    setSyncing(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (!integrated || !canView || !companyId) return undefined;

    let cancelled = false;

    const run = async () => {
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        if (!cancelled) timeoutRef.current = setTimeout(run, POLL_MS);
        return;
      }
      let nextDelay = POLL_MS;
      try {
        const res = await squareCatalogApi.syncStatus(companyId);
        if (cancelled) return;
        const s = res.data.data;
        const last = s?.lastsyncat && !s.lastsyncat.startsWith('0001') ? s.lastsyncat : null;
        const inprogress = !!s?.inprogress;
        setSyncing(inprogress);
        if (baselineRef.current === null) {
          baselineRef.current = last || '';
        } else if (last && last !== baselineRef.current) {
          setUpdated(true);
        }
        nextDelay = inprogress ? POLL_FAST_MS : POLL_MS;
      } catch (e) {
        if (cancelled) return;
        setSyncing(false);
      }
      if (!cancelled) timeoutRef.current = setTimeout(run, nextDelay);
    };

    run();

    return () => {
      cancelled = true;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [integrated, canView, companyId]);

  if (syncing) {
    return (
      <div className="mb-3 flex items-center gap-2 rounded-md border border-sky-300 bg-sky-50 px-3 py-2 text-sm text-sky-800 dark:border-sky-500/40 dark:bg-sky-500/10 dark:text-sky-200">
        <Spin size="small" />
        Syncing with Square…
      </div>
    );
  }
  if (updated) {
    return (
      <div className="mb-3 flex items-center justify-between rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-200">
        <span>Menu updated from Square.</span>
        <button
          type="button"
          onClick={() => {
            baselineRef.current = null;
            setUpdated(false);
            onRefresh();
          }}
          className="rounded-md bg-emerald-600 px-2 py-1 text-xs font-semibold text-white hover:bg-emerald-700"
        >
          Refresh
        </button>
      </div>
    );
  }
  return null;
};

export default SquareSyncActivityBand;
